import { sortBorrowMarketCollateralAssets } from '@/utils/borrowMarkets/sortBorrowMarketCollateralAssets'
import type { BorrowMarket, BorrowMarketAssetData } from '@/utils/internalAppSdk'
import { useMemo } from 'react'

export interface BorrowMarketRow {
  id: string
  market: BorrowMarket
  assetData: BorrowMarketAssetData
  collateralAssets: BorrowMarketAssetData[]
  tokenSymbol: string
  networkName: BorrowMarket['network']['name']
  protocolName: string
  name: string
  marketSizeUsd?: number
  availableToBorrowUsd?: number
  utilizationRate?: number
  borrowRate?: number
}

function isAcceptedCollateralForBorrowAsset(collateralAssetData: BorrowMarketAssetData, borrowAssetAddress: string) {
  if (!collateralAssetData.isCollateralEnabled) return false

  if (collateralAssetData.pairwiseRiskParameters) {
    return collateralAssetData.pairwiseRiskParameters[borrowAssetAddress] !== undefined
  }

  return collateralAssetData.defaultRiskParameters !== undefined
}

export function useBorrowMarkets(borrowMarketsData: BorrowMarket[] | null | undefined) {
  const borrowMarkets = useMemo(
    () =>
      (borrowMarketsData ?? []).flatMap((market) => {
        return market.assetsData
          .filter((assetData) => assetData.assetConfig.isBorrowEnabled)
          .map((assetData): BorrowMarketRow => {
            const id = `${market.network.name}-${market.marketId}-${assetData.asset.address}`
            const collateralAssets = market.assetsData.filter((collateralAssetData) =>
              isAcceptedCollateralForBorrowAsset(collateralAssetData, assetData.asset.address)
            )

            return {
              id,
              market,
              assetData,
              collateralAssets: sortBorrowMarketCollateralAssets(collateralAssets),
              tokenSymbol: assetData.asset.symbol,
              networkName: market.network.name,
              protocolName: market.protocol.name,
              name: market.name,
              marketSizeUsd: assetData.totalSupplied?.usd ? Number(assetData.totalSupplied.usd) : undefined,
              availableToBorrowUsd: assetData.availableToBorrow?.usd
                ? Number(assetData.availableToBorrow.usd)
                : undefined,
              utilizationRate: assetData.utilizationRate,
              borrowRate: assetData.borrowRate,
            }
          })
      }),
    [borrowMarketsData]
  )

  return { borrowMarkets }
}
